import { apiClient } from './apiClient';
import { apiUrl } from './apiUrls';

export type ProjectFile = {
    id: string;
    file: string; // URL
    thumbnail?: string;
    original_name: string;
    file_type: string;
}

export type Stage = {
    id: number;
    code: number;
    name: string;
}

export type Phase = {
    id: number;
    name: string;
    stages: Stage[];
}

export type Project = {
  id: string;
  name: string;
  description: string;
  files?: ProjectFile[];
  phase?: number | null;
  stage?: number | null;
}

type ProjectsResponse = {
    projects: Project[];
    count: number;
    next: string | null;
    previous: string | null;
}

export async function getProject(projectId: string): Promise<Project> {
    const response = await apiClient.get(apiUrl(`/api/projects/${projectId}/`));
    return response.data;
}

export async function getProjects(url = "/api/projects/"): Promise<ProjectsResponse> {
    const resolvedUrl = url.startsWith('http') ? url : apiUrl(url);
    const response = await apiClient.get(resolvedUrl);
    return {
        projects: response.data.results,
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
    }
}

export async function createProject(name: string, description: string = "", files?: File[]) : Promise<Project> {
    const fullUrl = apiUrl('/api/projects/');

    let body: FormData | Record<string, unknown>;
    const headers: Record<string, string> = {};

    if (files && files.length > 0) {
        const formData = new FormData();
        formData.append('name', name);
        formData.append('description', description);
        files.forEach(file => {
            formData.append('files', file);
        });
        body = formData;
        // Axios handles multipart/form-data headers automatically for FormData
    } else {
        body = {
            name: name,
            description: description
        };
    }

    const response = await apiClient.post(fullUrl, body, { headers });
    return response.data;
}

export async function updateProject(projectId: string, name?: string, description?: string, files?: File[], phase?: number | null, stage?: number | null): Promise<Project> {
    const fullUrl = apiUrl(`/api/projects/${projectId}/`);

    let body: FormData | Record<string, unknown>;
    const headers: Record<string, string> = {};

    if (files && files.length > 0) {
        const formData = new FormData();
        if (name !== undefined) formData.append('name', name);
        if (description !== undefined) formData.append('description', description);
        if (phase !== undefined) formData.append('phase', phase === null ? '' : String(phase));
        if (stage !== undefined) formData.append('stage', stage === null ? '' : String(stage));
        files.forEach(file => {
            formData.append('files', file);
        });
        body = formData;
    } else {
        body = {};
        if (name !== undefined) body.name = name;
        if (description !== undefined) body.description = description;
        if (phase !== undefined) body.phase = phase;
        if (stage !== undefined) body.stage = stage;
    }

    const response = await apiClient.patch(fullUrl, body, { headers });
    return response.data;
}

export async function deleteProjects(projectIds: string[]): Promise<void> {
    if (projectIds.length === 0) {
        return;
    }

    const baseUrl = apiUrl('/api/projects/');
    await Promise.all(
        projectIds.map(projectId =>
            apiClient.delete(
                `${baseUrl}${projectId}/`, 
            )
        )
    );
}

export async function renameProject(projectId: string, name: string): Promise<Project> {
    return updateProject(projectId, name);
}

export async function getPhases(): Promise<Phase[]> {
    const response = await apiClient.get<Phase[]>(apiUrl('/api/phases/'));
    return response.data;
}

export async function deleteProjectFile(fileId: string): Promise<void> {
    const fullUrl = apiUrl(`/api/files/${fileId}/`);
    await apiClient.delete(fullUrl);
}